import { useQuery } from 'react-query'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TrailMapResponse } from '../../models/TrailMapResponse.model'
import { TrailWayService } from '../../services/TrailWayService/TrailWayService'
import { Box, Button, Typography } from '@mui/material'
declare const L: any

type LatLng = [number, number]

type TrailMapProps = {
  trailId: number
}

/**
 * Extracts Leaflet-compatible polylines from the backend GeoJSON geometry.
 * The backend returns a FeatureCollection whose features are LineStrings.
 * Each LineString's coordinates are [lng, lat] pairs (GeoJSON spec),
 * so we swap them to [lat, lng] for Leaflet.
 */
function extractPolylinesFromGeometry(geometry: TrailMapResponse['geometry'] | null | undefined): LatLng[][] {
  if (!geometry?.features?.length) return []

  return geometry.features
    .filter((feature: any) => feature?.geometry?.type === 'LineString' && Array.isArray(feature.geometry.coordinates))
    .map((feature: any) => feature.geometry.coordinates.map(([lng, lat]: [number, number]) => [lat, lng] as LatLng))
}

// centered stand-in, the same size as the map, for loading and fallback states
const MapPlaceholder = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      height: 500,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: 2,
    }}
  >
    {children}
  </Box>
)

const TrailImage = ({ src, maxHeight }: { src: string; maxHeight: number }) => (
  <Box component="img" src={src} alt="Trail" sx={{ maxWidth: '100%', maxHeight, borderRadius: 1, boxShadow: 2 }} />
)

const GEOMETRY_RETRY_DELAY_MS = 5000
const GEOMETRY_MAX_RETRIES = 3

export const TrailMap: FC<TrailMapProps> = ({ trailId }) => {
  const [obstructions, setObstructions] = useState<any[]>([])
  const mapRef = useRef<any>(null)
  const overlaysRef = useRef<{ layerGroup: any } | null>(null)
  const geometryRetryCount = useRef(0)
  const trailWayService = useMemo(() => new TrailWayService(), [])
  const {
    isLoading,
    error: searchError,
    data: trailMapData,
    refetch,
  } = useQuery(['TrailWayService.getTrailMap', { trailId }], () => trailWayService.getTrailMap({ trailId }), {
    onError: (err) => {
      console.error('Error fetching trail map data:', err)
    },
  })

  // Backend geometry is returned alongside trail data — no separate fetch needed.
  const wayPolylines = useMemo(() => extractPolylinesFromGeometry(trailMapData?.geometry), [trailMapData?.geometry])

  // Reset retry count when the trail changes.
  useEffect(() => {
    geometryRetryCount.current = 0
  }, [trailId])

  // Cold-cache retry: When a trail has Way IDs but the backend returned no geometry,
  // the backend is likely fetching geometry from Overpass for the first time (newly created
  // trail). Retry a few times with a delay to give the backend time to cache it.
  const hasWayIds = (trailMapData?.trail?.ways?.length ?? 0) > 0
  const isRetryingGeometry =
    hasWayIds &&
    wayPolylines.length === 0 &&
    !isLoading &&
    !searchError &&
    geometryRetryCount.current < GEOMETRY_MAX_RETRIES

  useEffect(() => {
    if (!hasWayIds || wayPolylines.length > 0 || isLoading || searchError) return
    if (geometryRetryCount.current >= GEOMETRY_MAX_RETRIES) return

    const timer = setTimeout(() => {
      geometryRetryCount.current += 1
      refetch()
    }, GEOMETRY_RETRY_DELAY_MS)

    return () => clearTimeout(timer)
  }, [hasWayIds, wayPolylines.length, isLoading, searchError, trailMapData, refetch])

  const handleManualRetry = useCallback(() => {
    geometryRetryCount.current = 0
    refetch()
  }, [refetch])

  useEffect(() => {
    if (!trailMapData) return

    // Filter obstructions for valid coordinates
    const validObstructions = trailMapData.obstructions.filter((r: any) => {
      const lat = Number(r.gpsLat)
      const lng = Number(r.gpsLong)
      return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
    })
    setObstructions(validObstructions)
  }, [trailMapData])

  // Map rendering logic
  useEffect(() => {
    const hasData = wayPolylines.length > 0 || obstructions.length > 0
    if (!mapRef.current && hasData && typeof L !== 'undefined') {
      try {
        mapRef.current = L.map('map')
      } catch (error) {
        console.error('Error initializing map:', error)
        return
      }
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(mapRef.current)
    }
    if (!mapRef.current) return

    if (overlaysRef.current?.layerGroup) {
      overlaysRef.current.layerGroup.clearLayers()
    } else {
      overlaysRef.current = { layerGroup: L.layerGroup().addTo(mapRef.current) }
    }

    // Trail paths
    wayPolylines.forEach((line) => {
      L.polyline(line, { color: '#ff6b35', weight: 4 }).addTo(overlaysRef.current!.layerGroup)
    })

    // Obstructions
    const levelToColor = (level?: string): string => {
      const l = (level || '').toLowerCase()
      if (l === 'high') return '#f44336'
      if (l === 'medium') return '#FF9800'
      if (l === 'low') return '#2ecc71'
      return '#666'
    }

    obstructions.forEach((report) => {
      const lat = Number(report.gpsLat)
      const lng = Number(report.gpsLong)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      if (lat === 0 && lng === 0) return

      const color = levelToColor(report.alertLevel)
      const symbol = report.obstructionType?.symbol || '•'
      const iconHtml = `
        <div style="
          background: ${color};
          border: 3px solid white;
          border-radius: 50%;
          width: 40px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          cursor: pointer;
        ">
          <span>${symbol}</span>
        </div>`

      const icon = L.divIcon({
        html: iconHtml,
        className: '',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      })

      const rawTitle = report.obstructionType?.name || 'Obstruction'
      const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1)
      const alert = report.alertLevel || 'Unknown'
      let reportedAt = report.reportedAt
      reportedAt = new Date(report.reportedAt).toLocaleDateString()
      const popupHtml = `
        <div style="min-width:200px;">
          <div style="font-weight:800;">${report.obstructionType?.symbol} ${title}</div>
          <div>Alert level: ${alert}</div>
          <div>Reported: ${reportedAt}</div>
          <div>Location: ${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
          <div style="margin-top: 8px;">
            <a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none;">
              View on Google Maps
            </a>
          </div>
        </div>`

      L.marker([lat, lng], { icon }).addTo(overlaysRef.current!.layerGroup).bindPopup(popupHtml)
    })

    const obstructionPoints: LatLng[] = obstructions.map((r) => [Number(r.gpsLat), Number(r.gpsLong)])
    const allPoints: LatLng[] = [...wayPolylines.flat(), ...obstructionPoints]
    if (allPoints.length) {
      const bounds = L.latLngBounds(allPoints)
      mapRef.current.fitBounds(bounds, { padding: [16, 16] })
    }
  }, [wayPolylines, obstructions])

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  if (isLoading) {
    return (
      <MapPlaceholder>
        <Typography variant="body1">Loading trail map...</Typography>
      </MapPlaceholder>
    )
  }

  // Fallback: show trail image when geometry is unavailable
  const hasGeometry = wayPolylines.length > 0
  if (searchError || !hasGeometry) {
    // Show a loading indicator while retrying geometry fetch for newly created trails
    if (isRetryingGeometry) {
      return (
        <MapPlaceholder>
          <Typography variant="body1">Loading trail geometry...</Typography>
          <Typography variant="body2" color="grey.500" sx={{ mt: 1 }}>
            Waiting for geometry data (attempt {geometryRetryCount.current + 1}/{GEOMETRY_MAX_RETRIES})
          </Typography>
        </MapPlaceholder>
      )
    }

    // Trail has Way IDs configured but geometry didn't load — OSM data may not have
    // propagated to Overpass yet. Show a clear message and a manual retry button.
    if (hasWayIds && !searchError) {
      const imgUrl = trailMapData?.trail?.featuredImgUrl
      return (
        <MapPlaceholder>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            OSM geometry not yet available
          </Typography>
          <Typography variant="body2" color="grey.400" sx={{ mb: 2 }}>
            The trail map will appear once OpenStreetMap data propagates (usually within an hour).
          </Typography>
          <Button variant="outlined" onClick={handleManualRetry} sx={{ mb: imgUrl ? 3 : 0 }}>
            Retry
          </Button>
          {imgUrl && <TrailImage src={imgUrl} maxHeight={300} />}
        </MapPlaceholder>
      )
    }

    const imgUrl = trailMapData?.trail?.featuredImgUrl
    return (
      <MapPlaceholder>
        {!!searchError && (
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            Error loading trail map: {String(searchError)}
          </Typography>
        )}
        <Typography variant="body2" color="grey.400" sx={{ mb: 2 }}>
          Showing trail image fallback
        </Typography>
        {imgUrl ? (
          <TrailImage src={imgUrl} maxHeight={400} />
        ) : (
          <Typography variant="body1" color="grey.500">
            No image available
          </Typography>
        )}
      </MapPlaceholder>
    )
  }

  return <div id="map" style={{ height: 500, width: '100%' }}></div>
}
